import { supabase } from '@/lib/supabase/client'

// ── Shapes (mirror summarizeRun() output persisted in public.ops_reviews) ──────

export interface Trend { now: number; prev: number; pct: number | null }

export interface FunnelDay {
  day: string
  app_opened: number
  engaged: number
  signups: number
  checkout_started: number
  paid: number
  embed_save_cta: number
  embed_auth_done: number
  embed_upgrade_done: number
  dau: number
}

export interface Anomaly {
  source: string
  severity: 'crit' | 'warn' | 'info'
  code: string
  message: string
  metric: string | null
  value: number | null
  baseline: number | null
}

export interface Actionable {
  id: string
  source: string
  kind: string
  ref: string | null
  title: string | null
  hard: boolean
  url: string | null
  recommended_next_step: string
}

export type MetricBag = Record<string, Record<string, number | string>>

export interface OpsReview {
  id: string
  period_type: string
  review_date: string
  generated_at: string
  window_label: string
  window_since: string
  window_until: string
  mode: string
  source: string
  sources_healthy: number
  sources_total: number
  health_summary: string[]
  section_health: Record<string, boolean>
  metrics: MetricBag
  trends: Record<string, Record<string, unknown>>
  anomalies: Anomaly[]
  actionables: Actionable[]
  baselines: Record<string, number>
  insights: string[]
  html_url: string | null
}

export interface OpsReviewListRow {
  id: string
  period_type: string
  review_date: string
  generated_at: string
  window_label: string
  mode: string
  source: string
  sources_healthy: number
  sources_total: number
  section_health: Record<string, boolean>
  metrics: MetricBag
  html_url: string | null
  anomaly_counts: { crit: number; warn: number; info: number }
}

export interface OpsReviewRangeRow {
  review_date: string
  generated_at: string
  source: string
  sources_healthy: number
  sources_total: number
  section_health: Record<string, boolean>
  metrics: MetricBag
  trends: Record<string, Record<string, unknown>>
  anomalies: Anomaly[]
}

// ── Invoker ────────────────────────────────────────────────────────────────────

async function invokeOpsReview<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-ops-review', { body })
  if (error) throw new Error(String(error))
  if (data?.error) throw new Error(data.error)
  return data as T
}

export function fetchOpsReviewList(limit = 60) {
  return invokeOpsReview<{ rows: OpsReviewListRow[] }>({ action: 'list', limit }).then((d) => d.rows)
}

export function fetchOpsReviewDetail(reviewDate?: string) {
  return invokeOpsReview<{ review: OpsReview }>({ action: 'detail', review_date: reviewDate }).then((d) => d.review)
}

export function fetchOpsReviewRange(from: string, to: string) {
  return invokeOpsReview<{ rows: OpsReviewRangeRow[] }>({ action: 'range', from, to }).then((d) => d.rows)
}

export function triggerOpsReview(window = '1d', notify = false) {
  return invokeOpsReview<{ ok: boolean; status: number; result: unknown }>({ action: 'trigger', window, notify })
}
